import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';
import { t } from '@lingui/core/macro';
import { cva, type VariantProps } from 'class-variance-authority';

import ClearIcon from '@/assets/svg/bx-x.svg?react';
import { Button } from '@/components/buttons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/Tooltip';
import { cn } from '@/lib/utils';

export const badgeVariants = cva(
  'group/badge inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border font-medium whitespace-nowrap transition-all focus-visible:ring-2 focus-visible:ring-blue-200',
  {
    variants: {
      color: {
        default: '',
        success: '',
        critical: '',
        warning: '',
        info: '',
      },
      variant: {
        outlined: 'border',
        solid: 'border-transparent',
      },
      size: {
        small:
          'h-5 px-2 py-0.5 text-xs has-data-[icon=inline-end]:pr-1 has-data-[icon=inline-start]:pl-1',
        medium:
          'h-7 px-2.5 py-0.5 text-xs has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5',
        large:
          'h-8 px-3 py-1 text-sm has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5',
      },
      disabled: {
        true: 'cursor-not-allowed',
        false: '',
      },
    },
    compoundVariants: [
      {
        disabled: true,
        class: 'border-gray-200! bg-gray-200! text-gray-500!',
      },
      {
        color: 'default',
        variant: 'outlined',
        disabled: false,
        class: 'border-gray-300 bg-gray-100 text-gray-900',
      },
      {
        color: 'default',
        variant: 'solid',
        class: 'border-transparent bg-gray-300 text-gray-900',
      },
      {
        color: 'success',
        variant: 'outlined',
        class: 'border-green-400 bg-green-100 text-green-700',
      },
      {
        color: 'success',
        variant: 'solid',
        class: 'border-transparent bg-green-400 text-green-700',
      },
      {
        color: 'critical',
        variant: 'outlined',
        class: 'border-red-300 bg-red-100 text-red-700',
      },
      {
        color: 'critical',
        variant: 'solid',
        class: 'border-transparent bg-red-300 text-red-700',
      },
      {
        color: 'warning',
        variant: 'outlined',
        class: 'border-yellow-400 bg-yellow-100 text-yellow-700',
      },
      {
        color: 'warning',
        variant: 'solid',
        class: 'border-transparent bg-yellow-200 text-yellow-700',
      },
      {
        color: 'info',
        variant: 'outlined',
        class: 'border-aqua-400 bg-aqua-100 text-aqua-800',
      },
      {
        color: 'info',
        variant: 'solid',
        class: 'border-transparent bg-aqua-300 text-aqua-800',
      },
    ],
    defaultVariants: {
      color: 'default',
      variant: 'outlined',
      size: 'medium',
      disabled: false,
    },
  },
);

export type BadgeProps = useRender.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & {
    closable?: boolean;
    onClose?: (e: React.MouseEvent<HTMLElement>) => void;
    showTooltip?: boolean;
    disabled?: boolean;
    closeButtonInverse?: boolean;
  };

const CLOSE_BUTTON_SIZE_CLASS = {
  small: 'size-5',
  medium: 'size-5.5',
  large: 'size-6',
} as const;

const Badge = ({
  className,
  color = 'default',
  variant = 'outlined',
  size = 'medium',
  disabled = false,
  render,
  closable = false,
  onClose,
  showTooltip = true,
  closeButtonInverse = false,
  children,
  ...props
}: BadgeProps) => {
  const closeButton = closable && onClose && (
    <Button
      type="button"
      variant="ghost"
      inverse={closeButtonInverse}
      size="icon-small"
      shape="round"
      data-icon="inline-end"
      aria-label={t`Remove`}
      disabled={disabled}
      className={cn('-mr-0.5 min-w-0 p-0', CLOSE_BUTTON_SIZE_CLASS[size ?? 'medium'])}
      onClick={(e) => {
        e.stopPropagation();
        onClose(e);
      }}
    >
      <ClearIcon />
    </Button>
  );

  const badgeContent = useRender({
    defaultTagName: 'span',
    props: mergeProps<'span'>(
      {
        className: cn(badgeVariants({ className, color, variant, size, disabled })),
        'aria-disabled': disabled || undefined,
        children: closeButton ? (
          <>
            {children}
            {closeButton}
          </>
        ) : (
          children
        ),
      },
      props,
    ),
    render,
    state: {
      slot: 'badge',
      color,
      variant,
      size,
      disabled,
    },
  });

  if (showTooltip) {
    return (
      <Tooltip>
        <TooltipTrigger render={badgeContent} />
        <TooltipContent>{children}</TooltipContent>
      </Tooltip>
    );
  }

  return badgeContent;
};

export default Badge;
